import { createSelector } from "@reduxjs/toolkit"
import { useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"

import useCollection from "areas/collection/hoc/use-collection"
import {
  clearCategoryFilters,
  toggleFilterModal,
  toggleFilterPanel,
  updateFilter,
  updateUniqueFilter,
  updateCurrentSort,
} from "areas/collection/store/actions"

const selectCollectionProps = createSelector(
  [(state) => state.collection],
  (collection) => {
    const {
      applied_filters = [],
      loading,
      filters,
      openedPanel,
      sort,
    } = collection
    return {
      appliedFilters: applied_filters,
      currentSort: sort,
      filters,
      hasFilters: filters.some((filter) =>
        filter.categories.some((cat) => cat.is_active_filter)
      ),
      loading,
      openedPanel,
      tags: filters.reduce(
        (tags, filter) => [
          ...tags,
          ...filter.categories.filter((cat) => cat.is_active_filter),
        ],
        []
      ),
    }
  }
)

export default function withFilters(WrappedComponent) {
  const WithFilters = (props) => {
    const dispatch = useDispatch()
    const { data } = useCollection()

    const {
      appliedFilters,
      currentSort,
      filters,
      hasFilters,
      loading,
      openedPanel,
      tags,
    } = useSelector(selectCollectionProps)

    const sortMenu = data?.sort_menu || {}
    const currentSortOption = useMemo(() => {
      const options = data?.sort_menu?.options || []
      return options.find((opt) => opt.sort === currentSort) ?? options?.[0]
    }, [currentSort, data])

    const collectionProps = {
      appliedFilters,
      collectionFilters: filters,
      currentSort,
      currentSortOption,
      // disableFilters: data?.filter_menu?.disable_filters,
      // disableSort: sortMenu?.disable_sort,
      hasFilters,
      loading,
      openedPanel,
      sortMenu,
      tags,
    }

    const dispatchProps = {
      clearAllTags: (e) => {
        e.preventDefault()
        dispatch(clearCategoryFilters("all"))
      },
      removeFilter: (e, filter) => {
        e.preventDefault()
        dispatch(updateFilter(filter))
      },
      setFilter: (filter, unique, belongs_to) => {
        if (unique) {
          // deselect all other checkboxes in the category, then check the one requested
          dispatch(updateUniqueFilter({ belongs_to, filter }))
        } else {
          // Don't pass belongs_to for non-unique filters so siblings are not cleared
          dispatch(updateFilter(filter))
        }
      },
      toggleFilterModal: (show) => dispatch(toggleFilterModal(show)),
      toggleFilterPanel: (panel) => dispatch(toggleFilterPanel(panel)),
      updateSort: (option) => {
        dispatch(updateCurrentSort(option))
      },
    }

    return (
      <WrappedComponent {...collectionProps} {...dispatchProps} {...props} />
    )
  }

  return WithFilters
}
